import React from 'react';
import { Table, Button } from 'react-bootstrap';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { DailyReportResponseV4 } from '../../../../services/cm-group-report/response/DailyReportResponseV4';
import { IconArrowUp, IconArrowDown } from '@tabler/icons-react';

interface DailyReportTableV4Props {
    data: DailyReportResponseV4;
    orderBy: string;
    sort: 'asc' | 'desc';
    setOrderBy: (field: string) => void;
    setSort: (direction: 'asc' | 'desc') => void;
}

const DailyReportTableV4: React.FC<DailyReportTableV4Props> = ({ data, orderBy, sort, setOrderBy, setSort }) => {
    const columns = [
        { label: "Date", key: "date" },
        { label: "Rooms", key: "rooms" },
        { label: "Room Sold", key: "room_sold" },
        { label: "Daily Revenue", key: "daily_revenue" },
        { label: "RevPar", key: "revpar" },
        { label: "Occupancy (%)", key: "occupancy" },
        { label: "Available", key: "available" },
    ];

    const handleSort = (key: string) => {
        if (orderBy === key) {
            setSort(sort === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(key);
            setSort('asc'); // Default sort to ascending for daily report
        }
    };

    const renderSortIcon = (key: string) => {
        if (orderBy !== key) return null;
        return sort === 'asc' ? <IconArrowUp size={14} /> : <IconArrowDown size={14} />;
    };

    const formatRowValue = (row: any, colKey: string) => {
        const value = row[colKey as keyof typeof row];
        if (['daily_revenue', 'revpar'].includes(colKey)) {
            return formatPrice(Number(value) || 0);
        }
        if (colKey === 'occupancy') {
            return `${Number(value || 0).toFixed(2)}%`;
        }
        return value;
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Daily Report", 14, 10);
        const head = [columns.map(col => col.label)];
        const body = data.data.map(row => columns.map(col => formatRowValue(row, col.key)));
        const foot = [columns.map(col => {
            if (col.key === 'date') return 'Total';
            return formatRowValue(data.total, col.key);
        })];
        autoTable(doc, { head, body, foot });
        doc.save("DailyReportV4.pdf");
    };

    const exportToExcel = () => {
        const header = columns.map(col => col.label);
        const body = data.data.map(row => columns.map(col => formatRowValue(row, col.key)));
        const footer = columns.map(col => {
            if (col.key === 'date') return 'Total';
            return formatRowValue(data.total, col.key);
        });
        const wsData = [header, ...body, footer];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Daily Report");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "DailyReportV4.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>
            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th key={col.key} onClick={() => handleSort(col.key)} style={{ cursor: 'pointer' }}>
                                    {col.label} {renderSortIcon(col.key)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data?.data?.length > 0 ? (
                            data.data.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {columns.map((col) => (
                                        <td key={col.key}>{formatRowValue(row, col.key)}</td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan={columns.length} className="text-center">No data available</td></tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            {columns.map((col, index) => (
                                <td key={col.key}><strong>{index === 0 ? "Total" : formatRowValue(data.total, col.key)}</strong></td>
                            ))}
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default DailyReportTableV4;